import { auth } from "./firebase";

export interface UserProfile {
  name?: string;
  company?: string;
  jobRole?: string;
  onboardingCompleted?: boolean;
  timezone?: string;
  workingHours?: {
    [key: string]: {
      start: string;
      end: string;
      enabled: boolean;
    };
  };
  bufferTime?: number; // in minutes
  supportingDocsFolderId?: string;
  supportingDocsFolderName?: string;
  supportingDocsSourceName?: string;
}

export interface UserProfileUpdateData {
  name?: string;
  company?: string;
  jobRole?: string;
  onboardingCompleted?: boolean;
  timezone?: string;
  workingHours?: {
    [key: string]: {
      start: string;
      end: string;
      enabled: boolean;
    };
  };
  bufferTime?: number; // in minutes
  supportingDocsFolderId?: string;
  supportingDocsFolderName?: string;
  supportingDocsSourceName?: string;
}

class UserProfileServiceClass {
  private readonly BASE_URL = process.env.REACT_APP_FUNCTIONS_BASE_URL;

  /**
   * Update user profile information
   */
  async updateUserProfile(profileData: UserProfileUpdateData): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }

      const token = await user.getIdToken();
      const response = await fetch(`${this.BASE_URL}/updateUserProfile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update user profile");
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || "Failed to update user profile");
      }
    } catch (error) {
      console.error("Error updating user profile:", error);
      throw error;
    }
  }

  /**
   * Get user profile information
   */
  async getUserProfile(): Promise<UserProfile | null> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }

      const token = await user.getIdToken();
      const response = await fetch(`${this.BASE_URL}/getUserProfile`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get user profile");
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || "Failed to get user profile");
      }

      return result.profile;
    } catch (error) {
      console.error("Error getting user profile:", error);
      throw error;
    }
  }

  /**
   * Reset onboarding completion flag
   */
  async resetOnboarding(): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }

      const token = await user.getIdToken();
      const response = await fetch(`${this.BASE_URL}/updateUserProfile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ onboardingCompleted: false }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to reset onboarding");
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || "Failed to reset onboarding");
      }
    } catch (error) {
      console.error("Error resetting onboarding:", error);
      throw error;
    }
  }
}

export const userProfileService = new UserProfileServiceClass();
